import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Location } from 'history';

import ConfirmationAlert from '~/components/Alert/ConfirmationAlert';
import history from '~/services/history';
import { clearFilterParamsRequest } from '~/store/modules/tasks/actions';

interface NavigationData {
  canNavigate: boolean;
  setCanNavigate: (flag: boolean) => void;
  confirmationModal: boolean;
  setConfirmationModal: (flag: boolean) => void;
  customHeaderMessage: string | undefined;
  setCustomHeaderMessage: (newMessage: string) => void;
  location: Location | null;
  setLocation: (newLocation: Location) => void;
  confirmAndNavigate: (incomingAction: () => void) => void;
}

const NavigationContext = createContext<NavigationData>({} as NavigationData);

const NavigationProvider: React.FC = ({ children }) => {
  const [canNavigate, setCanNavigate] = useState<boolean>(true);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [location, setLocation] = useState<Location | null>(useLocation());
  const [action, setAction] = useState<(() => void) | null>(null);
  const [customHeaderMessage, setCustomHeaderMessage] = useState<string>();

  const dispatch = useDispatch();

  const setNewLocation = useCallback((newLocation: Location) => {
    setAction(null);
    setLocation(newLocation);
  }, []);

  const setNewAction = useCallback((newAction: (() => void) | null) => {
    setLocation(null);
    setAction(() => newAction);
  }, []);

  const confirmAndNavigate = useCallback(
    (incomingAction: () => void) => {
      if (canNavigate === false) {
        setNewAction(incomingAction);
        setConfirmationModal(true);
        return;
      }
      incomingAction();
      setNewAction(null);
    },
    [canNavigate, setNewAction],
  );

  useEffect(() => {
    if (canNavigate) {
      setConfirmationModal(false);
      if (action) {
        action();
      } else if (location) {
        history.push(location);
      }
      setNewAction(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canNavigate, action, location]);

  return (
    <NavigationContext.Provider
      value={{
        canNavigate,
        setCanNavigate,
        confirmationModal,
        setConfirmationModal,
        customHeaderMessage,
        setCustomHeaderMessage,
        location,
        setLocation: setNewLocation,
        confirmAndNavigate,
      }}
    >
      <ConfirmationAlert
        customHeaderMessage={customHeaderMessage}
        onOk={() => {
          setCanNavigate(true);
          dispatch(clearFilterParamsRequest());
        }}
        onCancel={() => {
          setConfirmationModal(false);
          setNewAction(null);
        }}
        open={confirmationModal}
        modalState={(opened: boolean) => setConfirmationModal(opened)}
      />

      {children}
    </NavigationContext.Provider>
  );
};

function useNavigation(data?: { customHeaderMessage: string }) {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error('useNavigation must be used within an NavigationProvider');
  }

  if (data) {
    const { customHeaderMessage } = data;
    context.setCustomHeaderMessage(customHeaderMessage);
  }

  return context;
}

export { NavigationProvider, useNavigation };
